import { useMemo } from "react";
import { Button, Table } from "react-bootstrap";
import { EditIcon } from "assets/icons/EditIcon";
import { CloseIcon } from "assets/icons/CloseIcon";
import { LISTA_COLORES_ICONOS } from "assets/icons/LISTA_COLORES_ICONOS";

// cuatrimestre: null/undefined = anual, 1 = 1er cuatrimestre, 2 = 2do cuatrimestre
export const CURSADO_OPTIONS = [
  { value: "", label: "Anual" },
  { value: 1, label: "1er cuatrimestre" },
  { value: 2, label: "2do cuatrimestre" },
];

export const labelCursado = (c?: number | null) =>
  c === 1 ? "1° Cuatr." : c === 2 ? "2° Cuatr." : "Anual";

// Carga horaria semanal de cada cuatrimestre: las anuales suman en ambos,
// las cuatrimestrales solo en el suyo. Único lugar donde se totalizan horas.
export const horasPorCuatrimestre = (
  espacios: { horasSemanales?: number | string; cuatrimestre?: number | null }[],
) =>
  espacios.reduce(
    (acc, e) => {
      const h = Number(e.horasSemanales) || 0;
      if (e.cuatrimestre !== 2) acc.c1 += h;
      if (e.cuatrimestre !== 1) acc.c2 += h;
      return acc;
    },
    { c1: 0, c2: 0 },
  );

export interface EspaciosTableRow {
  id?: number | string;
  nombre: string;
  anio?: number | string;
  horasSemanales?: number | string;
  cuatrimestre?: number | null;
  area?: string;
  tipoEspacio?: string;
  detalleModificacion?: string;
}

interface Props {
  espacios: EspaciosTableRow[];
  /** Si es true, no muestra columna de acciones */
  readonly?: boolean;
  /** Callback al editar un espacio (recibe el índice original) */
  onEdit?: (index: number) => void;
  /** Callback al eliminar un espacio (recibe el índice original) */
  onDelete?: (index: number) => void;
  /** Texto cuando no hay espacios */
  emptyMessage?: string;
  /** Mostrar columna "Detalle modificación" */
  showDetalleModificacion?: boolean;
  /** Altura máxima del contenedor con scroll */
  maxHeight?: string;
}

interface GroupedAnio {
  anio: number | string;
  rows: { espacio: EspaciosTableRow; originalIndex: number }[];
  totalHorasAnio: { c1: number; c2: number };
}

export const EspaciosTable = ({
  espacios,
  readonly = false,
  onEdit,
  onDelete,
  emptyMessage = "No se encontraron espacios.",
  showDetalleModificacion = false,
  maxHeight = "300px",
}: Props) => {
  const totalHoras = horasPorCuatrimestre(espacios);

  const showActions = !readonly && (onEdit || onDelete);
  const hasAnio = espacios.some((e) => e.anio !== undefined && e.anio !== null);

  // Agrupar espacios por año para hacer rowSpan
  const groupedByAnio: GroupedAnio[] = useMemo(() => {
    if (!hasAnio) return [];

    const groups = new Map<string | number, { espacio: EspaciosTableRow; originalIndex: number }[]>();
    
    // Ordenar por año y, dentro del año, anual -> 1C -> 2C, manteniendo el índice original
    const sorted = espacios
      .map((esp, idx) => ({ espacio: esp, originalIndex: idx }))
      .sort(
        (a, b) =>
          (Number(a.espacio.anio) || 0) - (Number(b.espacio.anio) || 0) ||
          (a.espacio.cuatrimestre ?? 0) - (b.espacio.cuatrimestre ?? 0)
      );

    for (const item of sorted) {
      const anio = item.espacio.anio ?? 0;
      if (!groups.has(anio)) {
        groups.set(anio, []);
      }
      groups.get(anio)!.push(item);
    }

    return Array.from(groups.entries()).map(([anio, rows]) => ({
      anio,
      rows,
      totalHorasAnio: horasPorCuatrimestre(rows.map((r) => r.espacio)),
    }));
  }, [espacios, hasAnio]);

  if (espacios.length === 0) {
    return (
      <p className="text-muted small text-center mb-0">{emptyMessage}</p>
    );
  }

  // Contar columnas para el colSpan del footer (sin contar Año ni Total hs 1C/2C)
  const dataCols =
    1 + // Nombre
    1 + // Hs Semanales
    1 + // Cursado
    1 + // Área
    1 + // Tipo Espacio
    (showDetalleModificacion ? 1 : 0) +
    (showActions ? 1 : 0);

  return (
    <div style={{ maxHeight, overflowY: "auto" }}>
      <Table bordered hover size="sm" className="mb-0">
        <thead className="table-light" style={{ position: "sticky", top: 0 }}>
          <tr>
            {hasAnio && <th className="text-center">Año</th>}
            <th>Nombre</th>
            <th className="text-center">Hs Semanales</th>
            <th className="text-center">Cursado</th>
            <th>Área</th>
            <th>Tipo Espacio</th>
            {showDetalleModificacion && <th>Detalle Modificación</th>}
            {showActions && (
              <th className="text-center" style={{ width: "90px" }}>
                Acciones
              </th>
            )}
            {hasAnio && (
              <>
                <th className="text-center" title="Total hs semanales del 1er cuatrimestre">
                  Total 1C
                </th>
                <th className="text-center" title="Total hs semanales del 2do cuatrimestre">
                  Total 2C
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {hasAnio
            ? // Renderizado agrupado por año con rowSpan
              groupedByAnio.map((group) =>
                group.rows.map((row, rowIdx) => (
                  <tr key={row.espacio.id ?? row.originalIndex}>
                    {rowIdx === 0 && (
                      <td
                        rowSpan={group.rows.length}
                        className="text-center align-middle fw-semibold"
                        style={{ backgroundColor: "#f8f9fa" }}
                      >
                        {group.anio}
                      </td>
                    )}
                    <td>{row.espacio.nombre}</td>
                    <td className="text-center">
                      {row.espacio.horasSemanales ?? "-"}
                    </td>
                    <td className="text-center">
                      {labelCursado(row.espacio.cuatrimestre)}
                    </td>
                    <td>{row.espacio.area ?? "-"}</td>
                    <td>{row.espacio.tipoEspacio ?? "-"}</td>
                    {showDetalleModificacion && (
                      <td>{row.espacio.detalleModificacion ?? "-"}</td>
                    )}
                    {showActions && (
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          {onEdit && (
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0"
                              onClick={() => onEdit(row.originalIndex)}
                              title="Editar"
                            >
                              <EditIcon
                                size={16}
                                color={LISTA_COLORES_ICONOS.DARK_GRAY}
                              />
                            </Button>
                          )}
                          {onDelete && (
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0"
                              onClick={() => onDelete(row.originalIndex)}
                              title="Eliminar"
                            >
                              <CloseIcon
                                size={22}
                                color={LISTA_COLORES_ICONOS.DARK_GRAY}
                              />
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                    {rowIdx === 0 && (
                      <>
                        <td
                          rowSpan={group.rows.length}
                          className="text-center align-middle fw-bold"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          {group.totalHorasAnio.c1}
                        </td>
                        <td
                          rowSpan={group.rows.length}
                          className="text-center align-middle fw-bold"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          {group.totalHorasAnio.c2}
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )
            : // Renderizado plano sin agrupación
              espacios.map((espacio, idx) => (
                <tr key={espacio.id ?? idx}>
                  <td>{espacio.nombre}</td>
                  <td className="text-center">
                    {espacio.horasSemanales ?? "-"}
                  </td>
                  <td className="text-center">
                    {labelCursado(espacio.cuatrimestre)}
                  </td>
                  <td>{espacio.area ?? "-"}</td>
                  <td>{espacio.tipoEspacio ?? "-"}</td>
                  {showDetalleModificacion && (
                    <td>{espacio.detalleModificacion ?? "-"}</td>
                  )}
                  {showActions && (
                    <td className="text-center">
                      <div className="d-flex align-items-center justify-content-center gap-1">
                        {onEdit && (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            onClick={() => onEdit(idx)}
                            title="Editar"
                          >
                            <EditIcon
                              size={16}
                              color={LISTA_COLORES_ICONOS.DARK_GRAY}
                            />
                          </Button>
                        )}
                        {onDelete && (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            onClick={() => onDelete(idx)}
                            title="Eliminar"
                          >
                            <CloseIcon
                              size={22}
                              color={LISTA_COLORES_ICONOS.DARK_GRAY}
                            />
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
        </tbody>
        <tfoot className="table-light">
          {hasAnio ? (
            // Totales debajo de las columnas Total 1C / Total 2C
            <tr>
              <td></td>
              <td colSpan={dataCols} className="text-end fw-bold">
                Total horas semanales:
              </td>
              <td className="text-center fw-bold">{totalHoras.c1}</td>
              <td className="text-center fw-bold">{totalHoras.c2}</td>
            </tr>
          ) : (
            <tr>
              <td className="text-end fw-bold">Total horas:</td>
              <td className="text-center fw-bold">
                1C: {totalHoras.c1} / 2C: {totalHoras.c2}
              </td>
              <td colSpan={dataCols - 2}></td>
            </tr>
          )}
        </tfoot>
      </Table>
    </div>
  );
};
